// The door into a workspace: pick (or create) the taxpayer's financial year.
// Provenance: the predecessor folded this into HomePage's entity table; the estate
// idiom gives it its own screen so /w/:clientId has somewhere honest to land.
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CalendarPlus } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'

import { postJson } from '../../api/client'
import { useClient, usePeriods } from '../../api/hooks'
import type { Period } from '../../api/types'
import { Btn, Modal } from '../../components/Modal'
import { showToast } from '../../components/toast'
import { useEvaluation } from '../../lib/access'

/** FY options offered by the creator: current and the four before it. */
function fyOptions(): { label: string; start: string; end: string }[] {
  const now = new Date()
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1
  return Array.from({ length: 5 }, (_, i) => {
    const y = startYear - i
    return {
      label: `FY ${y}-${String((y + 1) % 100).padStart(2, '0')}`,
      start: `${y}-04-01`,
      end: `${y + 1}-03-31`,
    }
  })
}

export function PeriodPicker() {
  const { clientId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { can } = useEvaluation()
  // doubles as the existence probe: a cross-tenant clientId 404s here and the
  // picker says so instead of rendering an empty-but-plausible list
  const clientQ = useClient(clientId ?? '')
  const periodsQ = usePeriods(clientId ?? '')
  const [creating, setCreating] = useState(false)
  const [choice, setChoice] = useState(fyOptions()[0])

  const createMut = useMutation({
    mutationFn: () =>
      postJson<Period>(`/clients/${clientId}/periods`, {
        label: choice.label,
        fy_start: choice.start,
        fy_end: choice.end,
      }),
    onSuccess: (p) => {
      void queryClient.invalidateQueries({ queryKey: ['periods', clientId] })
      setCreating(false)
      void navigate(`/w/${clientId}/${p.id}/upload`)
    },
    onError: (e) => showToast({ color: 'red', title: 'Could not create period', message: String(e) }),
  })

  return (
    <div className="mx-auto max-w-xl">
      <h1 className="mb-1 text-lg font-semibold">{clientQ.data?.legal_name ?? 'Workspace'}</h1>
      <p className="mb-4 text-sm text-muted-foreground">
        Pick the financial year to work in. Each year keeps its own uploads, runs and
        dispositions.
      </p>

      {clientQ.isError || periodsQ.isError ? (
        // a failed read must not render as an empty-but-plausible list — that
        // is how a 404 once turned into "No financial year yet" over a period
        // that existed, and a duplicate 409 on the retry
        <p className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {clientQ.isError
            ? 'This client could not be loaded — it may not exist in this organisation.'
            : `The financial years could not be loaded: ${String(periodsQ.error)}`}
        </p>
      ) : periodsQ.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (periodsQ.data ?? []).length === 0 ? (
        <p className="mb-4 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          No financial year yet — create the first one to start uploading.
        </p>
      ) : (
        <ul className="mb-4 divide-y divide-border rounded-md border border-border">
          {(periodsQ.data ?? []).map((p) => (
            <li key={p.id}>
              <Link
                to={`/w/${clientId}/${p.id}/upload`}
                className="flex items-center justify-between px-3 py-2.5 text-sm hover:bg-muted"
              >
                <span className="font-medium">{p.label}</span>
                <span className="text-xs text-muted-foreground">
                  {p.default_tolerance ? `tolerance ₹${Number(p.default_tolerance)}` : ''}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {can('periods', 'can_create') && (
        <Btn kind="primary" onClick={() => setCreating(true)}>
          <CalendarPlus size={16} /> New financial year
        </Btn>
      )}

      <Modal opened={creating} onClose={() => setCreating(false)} title="New financial year">
        <label className="mb-4 block text-sm">
          Financial year
          <select
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={choice.label}
            onChange={(e) => {
              const next = fyOptions().find((o) => o.label === e.target.value)
              if (next) setChoice(next)
            }}
          >
            {fyOptions().map((o) => (
              <option key={o.label} value={o.label}>{o.label}</option>
            ))}
          </select>
        </label>
        <div className="flex justify-end gap-2">
          <Btn onClick={() => setCreating(false)}>Cancel</Btn>
          <Btn kind="primary" loading={createMut.isPending} onClick={() => createMut.mutate()}>
            Create &amp; open
          </Btn>
        </div>
      </Modal>
    </div>
  )
}
